/**
 * WorkOrderDetailsTable / WorkOrderStatusBadge — shared rendering for one work order's
 * per-ad-type breakdown (+ issued/accepted dates) and its accepted/pending status chip.
 * Single-sourced so the campaign-wide `WorkOrderStatsBadge` toolbar popover and a
 * single-channel work order popover (`InfluencerRow`'s "Work order" chip) render the
 * same work order identically instead of two hand-rolled copies drifting apart.
 */
import AdTypeBadge from '@/components/AdTypeBadge';
import { GgcBadge } from '@/components/ui/ggc/badge';
import { niceNumber } from '@/lib/utils/misc';
import { formatLocalDateTime } from '@/lib/utils/timezone';
import { useI18nText } from '@/lib/utils/i18nText';

export interface WorkOrderDetailsTableProps {
  items: { type: string; count: number; price: number }[];
  issuedAt?: string;
  acceptedAt?: string;
  advertisingTypesMap: Record<string, string>;
}

export function WorkOrderDetailsTable({ items, issuedAt, acceptedAt, advertisingTypesMap }: WorkOrderDetailsTableProps) {
  const { t } = useI18nText();
  return (
    <>
      <table className="wo-details-table">
        <thead>
          <tr>
            <th>{t('Ad Type', '광고 유형')}</th>
            <th>{t('Count', '개수')}</th>
            <th>{t('Price', '가격')}</th>
          </tr>
        </thead>
        <tbody>
          {items.map((wi, j) => (
            <tr key={j}>
              <td><AdTypeBadge typeId={wi.type} label={advertisingTypesMap[wi.type] ?? wi.type} /></td>
              <td>{wi.count}</td>
              <td>{wi.price ? `$${niceNumber(wi.price)}` : '-'}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="wo-status-popover-dates">
        <span>{t('Issued', '발행')}: {issuedAt ? formatLocalDateTime(issuedAt) : '-'}</span>
        {acceptedAt && <span>{t('Accepted', '승인')}: {formatLocalDateTime(acceptedAt)}</span>}
      </div>
    </>
  );
}

export function WorkOrderStatusBadge({ status }: { status: 'accepted' | 'pending' }) {
  const { t } = useI18nText();
  return (
    <GgcBadge status={status === 'accepted' ? 'success' : 'muted'} hideDot>
      {status === 'accepted' ? t('Accepted', '승인됨') : t('Pending', '대기중')}
    </GgcBadge>
  );
}
